import { z } from "zod";
import { TaxCategory } from "./tax.js";

/**
 * 定期請求（#268 1.5a、../eigyouryoku/docs/12・§8 の決定（10/28）、docs/98。営業力 E1.5 → 現場力にも開く。門はモジュール sales）。
 *   案件（契約）の請求タイミングを「定期」にし、契約のテンプレート（件名・請求日・間隔・期間・前回の引用・備考・明細）を1つ付ける。
 *   月ごとに請求書の下書きを作る（1契約1か月1枚。DB の fn_create_invoice_from_recurring）。下書きは毎朝 6:00 に「請求日の n日前」を迎えた契約の分を自動で作る（会社の設定）。
 *   出すのは人（まとめて発行・PDF・メールは #267 3c の口）
 */

/** 月（YYYY-MM） */
export const RecurringMonth = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/);
export type RecurringMonth = z.infer<typeof RecurringMonth>;

/** 請求日の決まり：末日／月初（1日）／指定日 */
export const RecurringDayRule = z.enum(["month_end", "month_start", "day"]);
export type RecurringDayRule = z.infer<typeof RecurringDayRule>;
export const RECURRING_DAY_RULE_LABEL: Record<RecurringDayRule, string> = { month_end: "末日", month_start: "月初", day: "指定日" };

/** 間隔（か月）：毎月・2か月ごと・3か月ごと・6か月ごと・12か月ごと */
export const RECURRING_INTERVALS = [1, 2, 3, 6, 12] as const;
export const RecurringInterval = z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(6), z.literal(12)]);
export type RecurringInterval = z.infer<typeof RecurringInterval>;
export const recurringIntervalLabel = (n: number): string => (n === 1 ? "毎月" : `${n}か月ごと`);

/** 契約の状態：継続／停止（その月は対象外。再開できる）／終了 */
export const RecurringStatus = z.enum(["active", "paused", "ended"]);
export type RecurringStatus = z.infer<typeof RecurringStatus>;
export const RECURRING_STATUS_LABEL: Record<RecurringStatus, string> = { active: "継続", paused: "停止", ended: "終了" };

/** その月の状態：未作成／下書き／発行済／対象外（停止・終了・請求の月でない） */
export const RecurringMonthState = z.enum(["none", "draft", "issued", "skip"]);
export type RecurringMonthState = z.infer<typeof RecurringMonthState>;
export const RECURRING_MONTH_STATE_LABEL: Record<RecurringMonthState, string> = { none: "未作成", draft: "下書き", issued: "発行済", skip: "対象外" };

/**
 * 画面・API の文（{customer}・{project}・{lineName} は画面が t()、API が fillLabels で埋める）。
 * 件名のテンプレートの差し込み（{year}・{month}・{ym}・{customer}）は言葉の言い換えと同じ形なので、テンプレートの文は t() を通さない
 */
export const RECURRING_MSG = {
  title: "定期請求",
  back: "請求書一覧",
  help: "{project}の請求タイミングを「定期」にすると、契約のテンプレートから毎月の請求書の下書きを作ります。下書きの自動作成がオンなら、請求日の数日前の朝 6:00 に作ります。発行は請求書の一覧の「まとめて発行」で",
  /** 新しい{project}の請求タイミングの「定期」のカードの下の言葉と、「？」に足す一文 */
  timingCaption: "毎月の請求書",
  timingHelp: "定期にすると、契約のテンプレートから毎月の請求書の下書きを作れます。",
  /** 請求書の一覧の入口・{project}のタブ */
  listEntry: "定期請求",
  tab: "定期請求",
  colClient: "{customer}／契約",
  colDay: "請求日",
  colInterval: "間隔・次の請求月",
  colState: "状態",
  colSubtotal: "テンプレートの合計（税抜）",
  colMonth: (m: number): string => `${m}月分`,
  filter: "{customer}・契約で絞り込み",
  create: "下書き作成",
  /** 選んだ契約の下書きをまとめて作る（今月なら「今月分作成」） */
  createAll: (m: number, thisMonth: boolean): string => (thisMonth ? "今月分作成" : `${m}月分作成`),
  createMonth: (m: number): string => `${m}月分作成`,
  selected: (n: number): string => `${n}件 選択`,
  deselect: "選択解除",
  created: (n: number): string => `${n}件の下書きを作成しました`,
  createdOne: "下書きを作成しました",
  footer: (n: number): string => `${n}契約`,
  footerTotal: "月額の合計（税抜）",
  footerMonth: (m: number, none: number, draft: number, issued: number): string => `${m}月分：未作成 ${none}・下書き ${draft}・発行済 ${issued}`,
  next: (m: number): string => `次 ${m}月`,
  empty: "定期請求の契約はありません",
  emptyHelp: "{project}の画面で請求タイミングを「定期」にして、テンプレートを作成します",
  /* ---- 契約の画面のタブ ---- */
  edit: "修正",
  more: "定期請求のほかの操作",
  pause: "停止",
  resume: "再開",
  end: "終了",
  paused: "停止しました",
  resumed: "再開しました",
  ended: "終了しました",
  rowSubject: "件名のテンプレート",
  rowDay: "請求日",
  rowInterval: "間隔",
  rowPeriod: "期間",
  rowCopyLast: "前回の引用",
  rowMemo: "備考のテンプレート",
  copyLastOn: "する（数量・単価を前回の請求書から引用）",
  copyLastOff: "しない（テンプレートの数量・単価）",
  dayAuto: (label: string, lead: number): string => `${label}（請求日の${lead}日前に下書きを自動作成）`,
  linesTitle: "明細のテンプレート",
  linesHelp: "毎月の下書きに引用する明細です。数量が変わる月は下書きで修正します。前回の引用がオンなら、名称と{lineSpec}が同じ行の数量・単価を前回の請求書から引用します",
  linesEmpty: "明細のテンプレートはありません",
  linesEmptyCopy: "前回の請求書の明細をそのまま引用します",
  linesTotal: "合計（税抜）",
  historyTitle: "請求履歴",
  historyNext: (m: number, mmdd: string, lead: number): string => `${m}月分の下書きは ${mmdd}（請求日の${lead}日前）に自動作成`,
  historyEmpty: "請求書はありません",
  colHistMonth: "月",
  colHistInvoice: "請求書",
  colHistState: "状態",
  colHistIssue: "請求日",
  colHistAmount: "金額（税抜）",
  colHistPay: "入金",
  invoiceLink: "請求書",
  noPlan: "定期請求のテンプレートはありません",
  createPlan: "テンプレート作成",
  /* ---- テンプレートの窓 ---- */
  dialogTitle: "定期請求のテンプレート",
  /** 件名のテンプレートの差し込みの案内（{customer} は言葉の言い換えにしない＝ word に会社の言葉を渡す） */
  subjectHint: (word: string): string => `差し込み：{year}＝年・{month}＝月・{ym}＝2026年11月・{customer}＝${word}名`,
  subjectExample: (s: string): string => `例：${s}`,
  dayOfMonth: "指定日",
  startMonth: "開始月",
  endMonth: "終了月",
  noEnd: "終了月なし",
  addLine: "行追加",
  addGroup: "見出し追加",
  moveUp: "上へ",
  moveDown: "下へ",
  kind: { detail: "明細", group: "見出し", note: "文章" } as Record<"detail" | "group" | "note", string>,
  saved: "定期請求のテンプレートを保存しました",
  /* ---- 設定 → 見積・請求 の節 ---- */
  settingsSection: "定期請求",
  settingsHelp: "下書きの自動作成がオンなら、毎朝 6:00 に「請求日の n日前」を迎えた契約の請求書の下書きを作ります。発行・送信は人が行います",
  autoLabel: "下書きの自動作成",
  leadLabel: "作成時期",
  leadValue: (n: number): string => `請求日の${n}日前（毎朝 6:00）`,
  notifyLabel: "作成の通知",
  notifyValue: "ホーム「未請求」に「定期 n件（下書き）」",
  autoOn: "下書きの自動作成をオンにしました",
  autoOff: "下書きの自動作成をオフにしました",
  leadSaved: (n: number): string => `作成時期を「請求日の${n}日前」にしました`,
  /* ---- ホーム・請求書 ---- */
  homeDrafts: (n: number): string => `定期 ${n}件（下書き）`,
  /** 請求書の画面の帯：「2026年11月分・営業力 利用契約 ›」 */
  invoiceBand: (month: string, name: string): string => `${recurringMonthLabel(month)}・${name}`,
  /** 請求書の一覧のタブ */
  invoiceTab: "今月の定期請求",
  /* ---- 断り ---- */
  notFound: "契約が見つかりません",
  exists: "この月の請求書は作成済みです（作り直すときは下書きを削除してから）",
  notActive: "停止・終了の契約は作成できません",
  outOfRange: "この月は請求の月ではありません（開始月・間隔・終了月）",
  notRecurring: "{project}の請求タイミングが定期ではありません",
  planExists: "この{project}にはもう定期請求のテンプレートがあります",
  subjectRequired: "件名のテンプレートを入れてください",
  subjectTooLong: "件名のテンプレートは 200 文字までです",
  dayRequired: "指定日（1〜31日）を入れてください",
  endBeforeStart: "終了月は開始月と同じか後にしてください",
  lineNameRequired: "{lineName}を入れてください",
  tooManyLines: "明細のテンプレートは 100 行までです",
  memoTooLong: "備考のテンプレートは 2,000 文字までです",
} as const;

/** 明細のテンプレートの1行。kind＝明細／見出し／文章（見出し・文章は名前だけ） */
export const RecurringLineKind = z.enum(["detail", "group", "note"]);
export type RecurringLineKind = z.infer<typeof RecurringLineKind>;
export const RecurringLine = z.object({
  kind: RecurringLineKind.default("detail"),
  name: z.string().trim().min(1, RECURRING_MSG.lineNameRequired).max(200),
  spec: z.string().trim().max(200).default(""),
  /** 数量（小数2桁まで）。見出し・文章は null */
  quantity: z.number().min(-99999999).max(99999999).nullable().default(null),
  unit: z.string().trim().max(20).default(""),
  /** 単価（円・整数）。見出し・文章は null */
  unitPrice: z.number().int().min(-9999999999).max(9999999999).nullable().default(null),
  taxCategory: TaxCategory.default("standard"),
});
export type RecurringLine = z.infer<typeof RecurringLine>;

/** 明細のテンプレート（recurring_plans.lines_json）。schemaVersion で読み替える（見積のテンプレート estimate_templates と同じ作法） */
export const RecurringLines = z.object({
  schemaVersion: z.literal(1).default(1),
  lines: z.array(RecurringLine).max(100, RECURRING_MSG.tooManyLines).default([]),
});
export type RecurringLines = z.infer<typeof RecurringLines>;
/** 保存してある lines_json を読む（形が崩れていれば空） */
export function recurringLinesOf(json: unknown): RecurringLine[] {
  const r = RecurringLines.safeParse(json);
  return r.success ? r.data.lines : [];
}

/** 契約のテンプレート（GET /groups/:id/recurring の plan・PUT の答え） */
export const RecurringPlan = z.object({
  id: z.string().uuid(),
  groupId: z.string().uuid(),
  subjectTemplate: z.string(),
  dayRule: RecurringDayRule,
  dayOfMonth: z.number().int().nullable(),
  intervalMonths: z.number().int(),
  startMonth: RecurringMonth,
  endMonth: RecurringMonth.nullable(),
  copyLast: z.boolean(),
  memoTemplate: z.string(),
  /** 保存してある状態 */
  status: RecurringStatus,
  /** 画面の状態（終了月を過ぎた契約は終了） */
  state: RecurringStatus,
  lines: z.array(RecurringLine),
  /** 行ごとの金額（税抜。明細の行だけ 数量×単価 を会社の明細の丸めで丸めた値・見出しと文章は 0）。表示用（請求書の金額は作った下書きで DB が出す） */
  lineAmounts: z.array(z.number().int()),
  /** 会社の明細の丸めのコード（窓で打つあいだの金額の表示＝contract の roundAmount に渡す） */
  lineRounding: z.string(),
  /** テンプレートの合計（税抜。会社の明細の丸めで 数量×単価 を足す） */
  subtotal: z.number().int(),
  /** 版（project の updatedAt と同じ作法）。PUT に version として送り返す */
  updatedAt: z.string(),
});
export type RecurringPlan = z.infer<typeof RecurringPlan>;

/** テンプレートの保存（POST /groups/:id/recurring・PUT /recurring/:id） */
export const RecurringPlanSave = z
  .object({
    subjectTemplate: z.string().trim().min(1, RECURRING_MSG.subjectRequired).max(200, RECURRING_MSG.subjectTooLong),
    dayRule: RecurringDayRule,
    dayOfMonth: z.number().int().min(1).max(31).nullable().default(null),
    intervalMonths: RecurringInterval,
    startMonth: RecurringMonth,
    endMonth: RecurringMonth.nullable().default(null),
    copyLast: z.boolean().default(true),
    memoTemplate: z.string().max(2000, RECURRING_MSG.memoTooLong).default(""),
    lines: z.array(RecurringLine).max(100, RECURRING_MSG.tooManyLines).default([]),
    /** 版（PUT のとき。違えば 409 RECURRING_STALE） */
    version: z.string().max(40).optional(),
  })
  .superRefine((v, ctx) => {
    if (v.dayRule === "day" && v.dayOfMonth == null) ctx.addIssue({ code: "custom", path: ["dayOfMonth"], message: RECURRING_MSG.dayRequired });
    if (v.endMonth && v.endMonth < v.startMonth) ctx.addIssue({ code: "custom", path: ["endMonth"], message: RECURRING_MSG.endBeforeStart });
  });
export type RecurringPlanSave = z.input<typeof RecurringPlanSave>;

/** 請求履歴の1行（その契約の請求書。新しい月が上） */
export const RecurringHistoryRow = z.object({
  month: RecurringMonth,
  invoiceId: z.string().uuid(),
  documentNo: z.string().nullable(),
  state: z.enum(["draft", "issued"]),
  issueDate: z.string(),
  subtotal: z.number().int(),
  /** 入金（発行済だけ）：paid＝入金済（paidOn＝最後の入金日）／partial＝一部入金／unpaid＝未入金／overdue＝期限超過。下書きは null */
  pay: z.object({ state: z.enum(["unpaid", "partial", "paid", "overdue"]), paidOn: z.string().nullable() }).nullable(),
});
export type RecurringHistoryRow = z.infer<typeof RecurringHistoryRow>;

/** 会社の設定（下書きの自動作成・作成時期） */
export const RecurringAuto = z.object({ on: z.boolean(), leadDays: z.number().int().min(1).max(28) });
export type RecurringAuto = z.infer<typeof RecurringAuto>;

/** {project}（契約）の画面のタブ「定期請求」（GET /groups/:id/recurring） */
export const GroupRecurring = z.object({
  plan: RecurringPlan.nullable(),
  history: z.array(RecurringHistoryRow),
  /** 次に作れる月（今月から先の、請求の月でまだ請求書の無いいちばん早い月）。停止・終了・無ければ null＝「{m}月分作成」を出さない */
  nextMonth: RecurringMonth.nullable(),
  /** 自動で下書きを作る日（nextMonth の請求日 − 作成時期。自動オフ・nextMonth が無ければ null） */
  nextAutoOn: z.string().nullable(),
  auto: RecurringAuto,
});
export type GroupRecurring = z.infer<typeof GroupRecurring>;

/** 定期請求の一覧の1行（契約）＝GET /recurring?month= */
export const RecurringRow = z.object({
  planId: z.string().uuid(),
  group: z.object({ id: z.string().uuid(), name: z.string() }),
  client: z.object({ id: z.string().uuid(), name: z.string() }),
  dayRule: RecurringDayRule,
  dayOfMonth: z.number().int().nullable(),
  intervalMonths: z.number().int(),
  /** 表示している月より後の、いちばん早い請求の月。無ければ null */
  nextMonth: RecurringMonth.nullable(),
  state: RecurringStatus,
  subtotal: z.number().int(),
  monthState: RecurringMonthState,
  /** その月の請求書（下書きも発行済も）。無ければ null */
  invoice: z.object({ id: z.string().uuid(), documentNo: z.string().nullable(), subtotal: z.number().int() }).nullable(),
});
export type RecurringRow = z.infer<typeof RecurringRow>;
export const RecurringOverview = z.object({ month: RecurringMonth, thisMonth: RecurringMonth, rows: z.array(RecurringRow), auto: RecurringAuto });
export type RecurringOverview = z.infer<typeof RecurringOverview>;

/** POST /recurring/create-month：選んだ契約のその月の下書きをまとめて作る（契約ごとに1つの取引＝作れなかった契約があっても作れた分は残る） */
export const RecurringCreateMonth = z.object({ month: RecurringMonth, planIds: z.array(z.string().uuid()).min(1).max(200) });
export type RecurringCreateMonth = z.infer<typeof RecurringCreateMonth>;
export const RecurringCreateResult = z.object({
  created: z.array(z.object({ planId: z.string().uuid(), invoiceId: z.string().uuid() })),
  failed: z.array(z.object({ planId: z.string().uuid(), name: z.string(), code: z.string(), message: z.string() })),
});
export type RecurringCreateResult = z.infer<typeof RecurringCreateResult>;

/** 請求書の定期請求（Invoice.recurring）：契約と請求月（帯「2026年11月分・営業力 利用契約 ›」） */
export const InvoiceRecurring = z.object({ planId: z.string().uuid(), groupId: z.string().uuid(), groupName: z.string(), month: RecurringMonth });
export type InvoiceRecurring = z.infer<typeof InvoiceRecurring>;

/** ホームの「定期 n件（下書き）」：定期請求の下書きの数と、いちばん早い請求月 */
export const HomeRecurringDrafts = z.object({ count: z.number().int(), month: RecurringMonth });
export type HomeRecurringDrafts = z.infer<typeof HomeRecurringDrafts>;

/* ---------------- 計算（DB と同じ決まり。見本は下の CASES で両方を確かめる） ---------------- */

const ym = (month: string): { y: number; m: number } => {
  const [y, m] = month.split("-").map(Number) as [number, number];
  return { y, m };
};
const lastDay = (y: number, m: number): number => new Date(Date.UTC(y, m, 0)).getUTCDate();
const pad = (n: number): string => String(n).padStart(2, "0");

/** 請求日（DB の fn_recurring_issue_date と同じ）：末日／1日／指定日（その月に無い日は末日） */
export function recurringIssueDate(rule: RecurringDayRule, day: number | null, month: string): string {
  const { y, m } = ym(month);
  const d = rule === "month_start" ? 1 : rule === "day" ? Math.min(day ?? 1, lastDay(y, m)) : lastDay(y, m);
  return `${y}-${pad(m)}-${pad(d)}`;
}

/** 件名の差し込み（DB の fn_recurring_subject と同じ）：{year}・{month}・{ym}・{customer}。200 字で切る */
export function recurringSubject(template: string, month: string, customer: string): string {
  const { y, m } = ym(month);
  return template.split("{ym}").join(`${y}年${m}月`).split("{year}").join(String(y)).split("{month}").join(String(m)).split("{customer}").join(customer).slice(0, 200);
}

/** その月が請求の月か（DB の fn_recurring_in_cycle と同じ） */
export function recurringInCycle(start: string, end: string | null, interval: number, month: string): boolean {
  const i = (s: string) => ym(s).y * 12 + ym(s).m - 1;
  return month >= start && (!end || month <= end) && (i(month) - i(start)) % interval === 0;
}

/** 請求日の表示：末日／1日／15日 */
export function recurringDayLabel(rule: RecurringDayRule, day: number | null): string {
  return rule === "month_end" ? "末日" : rule === "month_start" ? "1日" : `${day ?? 1}日`;
}

/** 月の表示：「2026年11月分」 */
export function recurringMonthLabel(month: string): string {
  const { y, m } = ym(month);
  return `${y}年${m}月分`;
}

/** 期間の表示：「2026年11月 〜（終了月なし）」「2026年11月 〜 2027年3月」 */
export function recurringPeriodLabel(start: string, end: string | null): string {
  const a = ym(start);
  if (!end) return `${a.y}年${a.m}月 〜（${RECURRING_MSG.noEnd}）`;
  const b = ym(end);
  return `${a.y}年${a.m}月 〜 ${b.y}年${b.m}月`;
}

/** 画面の状態：終了月を過ぎた契約は終了（今月が終了月より後） */
export function recurringStateOf(status: RecurringStatus, endMonth: string | null, thisMonth: string): RecurringStatus {
  if (status === "ended") return "ended";
  if (endMonth && thisMonth > endMonth) return "ended";
  return status;
}

/** 請求日の見本（DB の test_recurring.sql ③ と API のテストで両方を確かめる） */
export const RECURRING_DATE_CASES: ReadonlyArray<{ rule: RecurringDayRule; day: number | null; month: string; want: string }> = [
  { rule: "month_end", day: null, month: "2026-11", want: "2026-11-30" },
  { rule: "month_end", day: null, month: "2026-02", want: "2026-02-28" },
  { rule: "month_start", day: null, month: "2026-12", want: "2026-12-01" },
  { rule: "day", day: 15, month: "2026-11", want: "2026-11-15" },
  { rule: "day", day: 31, month: "2027-02", want: "2027-02-28" },
  { rule: "day", day: 31, month: "2028-02", want: "2028-02-29" },
  { rule: "day", day: 31, month: "2026-04", want: "2026-04-30" },
];
/** 件名の差し込みの見本（DB の fn_recurring_subject と同じ答えになる） */
export const RECURRING_SUBJECT_CASES: ReadonlyArray<{ template: string; month: string; customer: string; want: string }> = [
  { template: "{year}年{month}月分 営業力 利用料", month: "2026-11", customer: "株式会社マッシュ", want: "2026年11月分 営業力 利用料" },
  { template: "{ym}分 保守料（{customer}）", month: "2027-01", customer: "株式会社ミヤビ商事", want: "2027年1月分 保守料（株式会社ミヤビ商事）" },
  { template: "{month}月 {month}月", month: "2026-03", customer: "", want: "3月 3月" },
  { template: "{nothing} 定額", month: "2026-03", customer: "", want: "{nothing} 定額" },
  { template: "駐車場 賃料", month: "2026-12", customer: "宝塚塗装工業", want: "駐車場 賃料" },
];
